<template>
    <div class="gallery-container">
      <!-- Page d'accueil -->
      <div v-if="homePage" class="home-container">
        <component 
          :is="dynamicComponent" 
          :homeImage="homeImage" 
          :exhibitIdProps="exhibitId"
          @exit="exitHomePage"
        />
      </div>
  
      <!-- Timeline View -->
      <div v-else class="timeline-container">
        <!-- Header -->
        <div class="gallery-header">
          <h1>{{ exhibitName }}</h1>
          
        </div>
  
        <!-- Timeline -->
        <div class="timeline" ref="timelineRef">
          <div class="timeline-line"></div>
          
          <div class="timeline-items">
            <div 
              v-for="(resource, index) in resources" 
              :key="index"
              class="timeline-item"
              :class="{ 'timeline-item-right': index % 2 === 0 }"
              @click="showModal(index)"
            >
              <!-- Node -->
              <div class="timeline-node"></div>
              
              <!-- Content -->
              <div class="timeline-content" :style="getAnimationDelay(index)">
                <div class="timeline-card">
                  <div class="timeline-image">
                    <img 
                      v-if="isImage(resource.url)"
                      :src="resource.url" 
                      :alt="resource.title"
                      loading="lazy"
                    />
                  </div>
                  <div class="timeline-info">
                    <h3>{{ resource.title }}</h3>
                    <p class="timeline-description">{{ resource.description == 'undefined' ? '' : truncateText(resource.description, 100) }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
  
        <!-- Modal -->
        <Teleport to="body">
          <div v-if="showModalFlag" class="modal" @click.self="closeModal">
            <div class="modal-content">
              <button class="close-button" @click="closeModal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
              
              <div class="modal-body">
                <h2>{{ selectedResource?.title }}</h2>
                <img 
                  v-if="selectedResource && isImage(selectedResource.url)"
                  :src="selectedResource.url" 
                  :alt="selectedResource.title"
                  class="modal-image"
                />
                <p class="modal-description">{{ selectedResource.description == 'undefined' ? '' : selectedResource.description }}</p>
              </div>
            </div>
          </div>
        </Teleport>
      </div>
    </div>
  </template>
  
  <script setup>
  import { ref, computed, onMounted, onUnmounted, defineProps } from 'vue';
  import { useRoute } from 'vue-router';
  import axios from 'axios';
  import Modele_1 from "../../components/Models/Modele_1.vue";
  import Modele_2 from "../../components/Models/Modele_2.vue";
  import Modele_3 from "../../components/Models/Modele_3.vue";
  import Modele_4 from "../../components/Models/Modele_4.vue";
  import Modele_5 from "../../components/Models/Modele_5.vue";
  import Modele_6 from "../../components/Models/Modele_6.vue";
  import Modele_7 from "../../components/Models/Modele_7.vue";
  

  const props = defineProps([
    'homePageProps',
    'exhibitId'
  ])
  // State
  const route = useRoute();
  const id = computed(() => route.params.id ?? props.exhibitId);
  const homePage = ref(props.homePageProps == false && props.exhibitId ? false : true);
  const resources = ref([]);
  const homeImage = ref({});
  const selectedTemplate = ref('');
  const showModalFlag = ref(false);
  const selectedResource = ref(null);
  const exhibitName = ref('');
  const userName = ref('');
  const timelineRef = ref(null);
  
  // Component mapping
  const modeleComponents = {
    Modele_1,
    Modele_2,
    Modele_3,
    Modele_4,
    Modele_5,
    Modele_6,
    Modele_7
  };
  
  // Computed
  const dynamicComponent = computed(() => {
    return modeleComponents[selectedTemplate.value] || null;
  });
  
  // Methods
  const truncateText = (text, length = 100) => {
    if (!text) return '';
    return text.length > length ? `${text.substring(0, length)}...` : text;
  };
  
  const isImage = (url) => {
    if (!url) return false;
    return /\.(jpg|jpeg|png|gif|webp)$/i.test(url.split('?')[0].split('/').pop());
  };
  
  const getAnimationDelay = (index) => {
    return { animationDelay: `${index * 0.2}s` };
  };
  
  // Intersection Observer for animation
  const setupIntersectionObserver = () => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };
  
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('timeline-item-visible');
        }
      });
    }, options);
  
    const timelineItems = document.querySelectorAll('.timeline-item');
    timelineItems.forEach(item => observer.observe(item));
  
    return observer;
  };
  
  // API calls
  const fetchData = async () => {
    try {
      const [homeImageRes, modelRes, exhibitRes, userRes] = await Promise.all([
        axios.get(`${process.env.VUE_APP_BASEURL}/exhibit_home/${id.value}`),
        axios.get(`${process.env.VUE_APP_BASEURL}/model/${id.value}`),
        axios.get(`${process.env.VUE_APP_BASEURL}/exhibit/${id.value}`),
        axios.get(`${process.env.VUE_APP_BASEURL}/user/${id.value}`)
      ]);
  
      homeImage.value = homeImageRes?.data[0];
      exhibitName.value = exhibitRes.data.exhibit_name;
      userName.value = userRes.data.organisation;
      
      if (modelRes.data.length > 0) {
        selectedTemplate.value = modelRes.data[0].model
          .replace(/è/g, 'e')
          .replace(/\s+/g, '_');
      }
  
      // Fetch resources
      const [resourcesRes, galleryRes] = await Promise.all([
        axios.post(`${process.env.VUE_APP_BASEURL}/resources`, { exhibitId: id.value }),
        axios.get(`${process.env.VUE_APP_BASEURL}/galleryResources/${id.value}`)
      ]);
  
      const galleryResourceIds = galleryRes.data.map(res => res.resource_id);
      const filteredResources = resourcesRes.data
        .filter(resource => galleryResourceIds.includes(resource.id));
  
      // Fetch resource URLs
      resources.value = await Promise.all(
        filteredResources.map(async (resource) => {
          const urlRes = await axios.get(
            `${process.env.VUE_APP_BASEURL}/s3Resource/${resource.name}`
          );
          return {
            url: urlRes.data,
            description: resource.description,
            title: resource.title,
            name: resource.name
          };
        })
      );
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };
  
  // Event handlers
  const exitHomePage = () => {
    homePage.value = false;
    // Setup animation observer after transition
    setTimeout(() => {
      observer.value = setupIntersectionObserver();
    }, 100);
  };
  
  const showModal = (index) => {
    if (resources.value[index]) {
      selectedResource.value = resources.value[index];
      showModalFlag.value = true;
    }
  };
  
  const closeModal = () => {
    showModalFlag.value = false;
    selectedResource.value = null;
  };
  
  // Lifecycle
  let observer = ref(null);
  
  onMounted(async () => {
    await fetchData();
    console.log("homePageProps: ", props.homePageProps)
    console.log("exhibitId: ", props.exhibitId)
    
    if(props.homePageProps == false && props.exhibitId) {
      console.log("ok")
      setTimeout(() => {
        observer.value = setupIntersectionObserver();
      }, 100);
    }

  });
  
  onUnmounted(() => {
    if (observer.value) {
      observer.value.disconnect();
    }
  });
  </script>
  
  <style scoped>
  .gallery-container {
    min-height: 100vh;
    background-color: #f5f5f5;
  }
  
  .gallery-header {
    text-align: center;
    padding: 2rem;
    background-color: white;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
  
  .gallery-header h1 {
    margin: 0;
    color: #2c3e50;
    font-size: 2rem;
  }
  
  .timeline-container {
    padding: 2rem 0;
    position: relative;
  }
  
  .timeline {
    position: relative;
    max-width: 1200px;
    margin: 0 auto;
    padding: 2rem;
  }
  
  .timeline-line {
    content: '';
    position: absolute;
    width: 6px;
    background-color: #2c3e50;
    top: 0;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 3px;
  }
  
  .timeline-items {
    position: relative;
  }
  
  .timeline-item {
    margin: 2rem 0;
    position: relative;
    width: 50%;
    opacity: 0;
    transform: translateX(-100px);
    transition: all 0.5s ease;
  }
  
  .timeline-item-right {
    left: 50%;
    transform: translateX(100px);
  }
  
  .timeline-item-visible {
    opacity: 1;
    transform: translateX(0);
  }
  
  .timeline-node {
    position: absolute;
    width: 20px;
    height: 20px;
    background-color: #2c3e50;
    border-radius: 50%;
    top: 50%;
    right: -10px;
    transform: translateY(-50%);
  }
  
  .timeline-item-right .timeline-node {
    left: -10px;
  }
  
  .timeline-content {
    padding: 1rem;
    position: relative;
    margin-right: 2rem;
  }
  
  .timeline-item-right .timeline-content {
    margin-right: 0;
    margin-left: 2rem;
  }
  
  .timeline-card {
    background: white;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
    transition: transform 0.3s ease;
  }
  
  .timeline-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 6px 12px rgba(0,0,0,0.15);
  }
  
  .timeline-image {
    width: 100%;
    height: 200px;
    overflow: hidden;
  }
  
  .timeline-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .timeline-info {
    padding: 1rem;
  }
  
  .timeline-info h3 {
    margin: 0 0 0.5rem;
    color: #2c3e50;
  }
  
  .timeline-description {
    color: #666;
    font-size: 0.9rem;
    margin: 0;
  }
  
  .modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0,0,0,0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }
  
  .modal-content {
    background: white;
    border-radius: 8px;
    max-width: 90%;
    max-height: 90vh;
    overflow-y: auto;
    position: relative;
    padding: 2rem;
  }
  
  .close-button {
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0.5rem;
    color: #666;
  }
  
  .modal-body {
    text-align: center;
  }
  
  .modal-image {
    max-width: 100%;
    height: auto;
    margin: 1rem 0;
    border-radius: 4px;
  }
  
  .modal-description {
    text-align: left;
    line-height: 1.6;
    color: #333;
  }
  
  @media (max-width: 768px) {
    .timeline-line {
      left: 2rem;
    }
    
    .timeline-item {
      width: 100%;
      padding-left: 3rem;
    }
    
    .timeline-item-right {
      left: 0;
    }
    
    .timeline-node {
      left: 2rem;
    }
    
    .timeline-content {
      margin: 0;
    }
  }
  </style>